<div class="container-fluid mt-3">
  <!-- Loading -->
  <div *ngIf="loading" class="text-center py-5">
    <i class="fa fa-spinner fa-spin fa-2x"></i>
    <p class="mt-2 text-muted">Loading task...</p>
  </div>

  <!-- Error -->
  <div *ngIf="error" class="alert alert-danger">{{ error }}</div>

  <!-- Task Form -->
  <div *ngIf="task && !loading">
    <!-- Case Summary -->
    <div class="d-flex justify-content-between align-items-start mb-3">
      <div>
        <h4 class="mb-0">
          <a href="javascript:void(0)" (click)="navigateToPacket()" class="text-decoration-none">
            {{ task.candidateName }} - {{ getWorkflowDisplayName(task.workflowType) }}
          </a>
        </h4>
        <div *ngIf="positionDisplay" class="text-muted" style="font-size: 0.9rem;">
          {{ positionDisplay }}
        </div>
        <div class="text-muted mt-1" style="font-size: 0.9rem;">
          Program Year &raquo; {{ formatYear(task.periodOfReviewId) }}
        </div>
        <div class="text-muted" style="font-size: 0.9rem;">
          Period Of Review &raquo; {{ extractDateFromUtcString(task.periodOfReviewStartDate) | date:'mediumDate' }} - {{ extractDateFromUtcString(task.periodOfReviewEndDate) | date:'mediumDate' }}
        </div>        
        <div class="text-muted" style="font-size: 0.9rem;">
          Case #{{ task.caseId }} &middot; Created {{ task.createdAt | date:'mediumDate' }}
        </div>
      </div>
      <div class="text-right">
        <span class="badge" [ngClass]="getCaseStatusClass(task.caseStatus)">
          {{ task.caseStatus }}
        </span>
        <div *ngIf="task.result" class="text-muted mt-1" style="font-size: 0.85rem;">
          {{ task.result }}
        </div>
      </div>
    </div>

    <hr class="mt-2 mb-3">

    <!-- Task Header -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div>
        <h5 class="mb-0">{{ getStageName(task.stage) }} Task</h5>
        <small class="text-muted">
          Assigned to {{ task.assignedToName || task.assignedToEmail }}
          <ng-container [ngSwitch]="true">
            <ng-container *ngSwitchCase="!!task.completedAt"> &middot; Completed {{ task.completedAt | date:'mediumDate' }}</ng-container>
            <ng-container *ngSwitchCase="isUpcoming && !!task.gateOpenDate"> &middot; Opens on {{ extractDateFromUtcString(task.gateOpenDate) | date:'mediumDate' }}</ng-container>
            <ng-container *ngSwitchCase="!isUpcoming && !!task.gateDueDate"> &middot; Due {{ extractDateFromUtcString(task.gateDueDate) | date:'mediumDate' }}</ng-container>
            <ng-container *ngSwitchDefault> &middot; Created {{ task.createdAt | date:'mediumDate' }}</ng-container>
          </ng-container>
        </small>
      </div>
      <span class="badge" [ngClass]="getTaskStatusClass(task.status)">{{ task.status }}</span>
    </div>

    <div *ngIf="task.completedAt" class="alert alert-info">
      This task was completed on {{ task.completedAt | date:'medium' }}.
      <span *ngIf="isReadOnly">You are viewing it in read-only mode.</span>
      <!-- Editable + past due means an admin is bypassing the closed window — don't
           imply the window is still open. -->
      <span *ngIf="!isReadOnly && isPastDue">The window closed<ng-container *ngIf="task.gateDueDate"> on {{ task.gateDueDate | date:'mediumDate' }}</ng-container>. You are editing as an administrator.</span>
      <span *ngIf="!isReadOnly && !isPastDue">It can still be revised until the window closes<ng-container *ngIf="task.gateDueDate"> on {{ task.gateDueDate | date:'mediumDate' }}</ng-container>.</span>
      <div *ngIf="canRescind" class="mt-2">
        <button type="button" class="btn btn-outline-danger btn-sm" (click)="rescind()">
          Rescind Submission
        </button>
      </div>
    </div>

    <div *ngIf="isUpcoming" class="alert alert-info">
      This step has not opened yet<ng-container *ngIf="task.gateOpenDate">. It opens on {{ task.gateOpenDate | date:'mediumDate' }}</ng-container>.
    </div>

    <div *ngIf="isPastDue && !task.completedAt" class="alert alert-warning">
      This step was due<ng-container *ngIf="task.gateDueDate"> on {{ task.gateDueDate | date:'mediumDate' }}</ng-container> and is past due.
      <span *ngIf="isReadOnly">It can no longer be completed. Contact AHR if you need an extension.</span>
      <span *ngIf="!isReadOnly">As an administrator, you can still complete it.</span>
    </div>

    <form *ngIf="form" [formGroup]="form" (ngSubmit)="submit()">
      <div *ngFor="let field of task.formSchema" class="mb-3">

        <!-- Text input -->
        <div *ngIf="field.type === 'text'" class="form-group">
          <label [for]="field.name">
            {{ field.label }}
            <span *ngIf="field.required" class="text-danger">*</span>
          </label>
          <input
            [id]="field.name"
            type="text"
            class="form-control"
            [formControlName]="field.name"
            [class.is-invalid]="getFieldError(field)">
          <div *ngIf="getFieldError(field)" class="invalid-feedback">
            {{ getFieldError(field) }}
          </div>
        </div>

        <!-- Textarea -->
        <div *ngIf="field.type === 'textarea'" class="form-group">
          <label [for]="field.name">
            {{ field.label }}
            <span *ngIf="field.required" class="text-danger">*</span>
          </label>
          <textarea
            [id]="field.name"
            class="form-control"
            rows="5"
            [formControlName]="field.name"
            [class.is-invalid]="getFieldError(field)"></textarea>
          <div *ngIf="getFieldError(field)" class="invalid-feedback">
            {{ getFieldError(field) }}
          </div>
        </div>

        <!-- Select -->
        <div *ngIf="field.type === 'select'" class="form-group">
          <label [for]="field.name">
            {{ field.label }}
            <span *ngIf="field.required" class="text-danger">*</span>
          </label>
          <select
            [id]="field.name"
            class="form-control"
            [formControlName]="field.name"
            [class.is-invalid]="getFieldError(field)">
            <option value="">-- Select --</option>
            <option *ngFor="let opt of field.options" [value]="opt.value">
              {{ opt.label }}
            </option>
          </select>
          <div *ngIf="getFieldError(field)" class="invalid-feedback">
            {{ getFieldError(field) }}
          </div>
        </div>

        <!-- Checkbox -->
        <div *ngIf="field.type === 'checkbox'" class="form-check">
          <input
            [id]="field.name"
            type="checkbox"
            class="form-check-input"
            [formControlName]="field.name">
          <label class="form-check-label" [for]="field.name">
            {{ field.label }}
          </label>
        </div>

        <!-- Date -->
        <div *ngIf="field.type === 'date'" class="form-group">
          <label [for]="field.name">
            {{ field.label }}
            <span *ngIf="field.required" class="text-danger">*</span>
          </label>
          <input
            [id]="field.name"
            type="date"
            class="form-control"
            [formControlName]="field.name"
            [class.is-invalid]="getFieldError(field)">
          <div *ngIf="getFieldError(field)" class="invalid-feedback">
            {{ getFieldError(field) }}
          </div>
        </div>

        <!-- Document List -->
        <div *ngIf="field.type === 'documentList'" class="form-group">
          <label>{{ field.label }}</label>
          <div *ngIf="getDocumentList(field.name).length === 0" class="text-muted small">
            No documents
          </div>
          <div *ngFor="let doc of getDocumentList(field.name)" class="border rounded p-2 mb-2">
            <div class="d-flex align-items-center justify-content-between">
              <div>
                <i class="fa fa-fw fa-file-o mr-1"></i>
                <strong>{{ doc.name }}</strong>
              </div>
              <span *ngIf="getAttachmentsForSlot(doc.name).length > 0"
                class="badge badge-success"><i class="fa fa-check mr-1"></i>Uploaded</span>
              <span *ngIf="getAttachmentsForSlot(doc.name).length === 0 && doc.minCount > 0"
                class="badge badge-warning">Required</span>
              <span *ngIf="getAttachmentsForSlot(doc.name).length === 0 && doc.minCount === 0"
                class="badge badge-secondary">Optional</span>
            </div>
            <div *ngIf="doc.note" class="text-muted small mt-1" [innerHTML]="doc.note"></div>

            <!-- Attachments for this document slot -->
            <div *ngFor="let att of getAttachmentsForSlot(doc.name)" class="d-flex align-items-center mt-1">
              <i class="fa fa-paperclip mr-2 text-muted"></i>
              <span class="mr-2">{{ att.fileName }}</span>
              <small class="text-muted mr-2">({{ ((att.sizeBytes || 0) / 1024) | number:'1.0-0' }} KB)</small>
              <button type="button"
                class="btn btn-sm btn-primary mr-1"
                style="padding: 0 0.2rem;"
                (click)="viewAttachment(att)">View</button>
              <button type="button"
                class="btn btn-sm btn-success mr-1"
                style="padding: 0 0.2rem;"
                (click)="downloadAttachment(att)">Download</button>
              <button
                *ngIf="!isReadOnly"
                type="button"
                class="btn btn-sm btn-danger"
                style="padding: 0 0.2rem;"
                (click)="deleteAttachment(att)">Delete</button>
            </div>
            <div *ngIf="getAttachmentsForSlot(doc.name).length === 0" class="small text-muted mt-1">
              <i class="fa fa-paperclip mr-1"></i>
              No files uploaded
            </div>

            <!-- Per-slot upload -->
            <input
              *ngIf="!isReadOnly"
              type="file"
              class="form-control-file mt-1"
              (change)="onFileUpload($event, doc.name)">
          </div>
        </div>

        <!-- Committee Members (display-only) -->
        <div *ngIf="field.type === 'committeeMembers'" class="form-group">
          <label>{{ field.label }}</label>
          <div *ngIf="getCommitteeMembers(field.name).length === 0" class="text-muted small">
            No committee members
          </div>
          <div *ngFor="let member of getCommitteeMembers(field.name)" class="d-flex align-items-center mb-2">
            <i class="fa fa-fw fa-user mr-2 text-muted"></i>
            <div>
              <div>{{ member.displayName || member.name }}</div>
              <div *ngIf="member.email" class="text-muted small">{{ member.email }}</div>
            </div>
          </div>
        </div>

        <!-- File upload (only shown when no documentList handles uploads) -->
        <div *ngIf="field.type === 'file' && !hasDocumentList" class="form-group">
          <label>
            {{ field.label }}
            <span *ngIf="field.required" class="text-danger">*</span>
          </label>

          <!-- Existing attachments for this slot -->
          <div *ngFor="let att of getAttachmentsForSlot(field.name)" class="d-flex align-items-center mb-1">
            <i class="fa fa-file mr-2 text-muted"></i>
            <span class="mr-2">{{ att.fileName }}</span>
            <small class="text-muted mr-2">({{ ((att.sizeBytes || 0) / 1024) | number:'1.0-0' }} KB)</small>
            <button type="button"
              class="btn btn-sm btn-primary mr-1"
              style="padding: 0 0.2rem;"
              (click)="viewAttachment(att)">View</button>
            <button type="button"
              class="btn btn-sm btn-success mr-1"
              style="padding: 0 0.2rem;"
              (click)="downloadAttachment(att)">Download</button>
            <button
              *ngIf="!isReadOnly"
              type="button"
              class="btn btn-sm btn-danger"
              style="padding: 0 0.2rem;"
              (click)="deleteAttachment(att)">Delete</button>
          </div>

          <!-- Upload input -->
          <input
            *ngIf="!isReadOnly"
            type="file"
            class="form-control-file mt-1"
            (change)="onFileUpload($event, field.name)">
        </div>

      </div>

      <!-- Actions -->
      <div class="mt-4 mb-4 d-flex">
        <button
          type="button"
          class="btn btn-outline-secondary mr-2"
          (click)="cancel()">
          {{ isReadOnly ? 'Back' : 'Cancel' }}
        </button>
        <button
          *ngIf="!isReadOnly"
          type="submit"
          class="btn btn-primary"
          [disabled]="submitting">
          <i *ngIf="submitting" class="fa fa-spinner fa-spin mr-1"></i>
          {{ submitting ? 'Submitting...' : 'Submit' }}
        </button>
      </div>
    </form>
  </div>
</div>
